import {
  Box,
  Typography,
  Stack,
  Button,
  Backdrop,
  CircularProgress,
  Table,
  TableFooter,
  TableRow,
  TablePagination,
} from "@mui/material";
import {
  ActiveProfileIcon,
  InactiveProfileIcon,
  CalendarIcon,
  TimeIcon,
} from "../AppointmentsIcons";
import Loader from "../../Common/assets/Loader";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { useState } from "react";
import {
  NoDataContainer,
  pageSize,
  paginationLabel,
  TablePaginationStyle,
} from "../../Common/styles/table";
import { DateTime } from "luxon";

const Appointments = ({
  loading,
  type,
  appointmentsData,
  refreshPage,
  openDrawer,
  totalAppointmentCount,
  page,
  handleChangePage,
}: any) => {
  const [loadingAppointmentId, setLoadingAppointmentId] = useState<
    string | null
  >(null);
  const studyId = localStorage.getItem("study-id");

  const handleCancel = async (selectedEntry: any) => {
    setLoadingAppointmentId(selectedEntry?.id);

    try {
      const res: AxiosResponse = await http.post(
        `/participant/study/${studyId}/appointments/${selectedEntry?.id}/cancel`
      );
      setLoadingAppointmentId(null);
      toastMessage("success", res.data.message);
      refreshPage();
    } catch (error) {
      setLoadingAppointmentId(null);
      errorToastMessage(error as Error);
    }
  };

  const handleJoinCall = async (appointmentId: any) => {
    window.open(`/telehealth/${studyId}/${appointmentId}`, "_blank");
  };

  return (
    <Box>
      {loading ? (
        <Backdrop
          open={true}
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <Loader />
        </Backdrop>
      ) : (
        appointmentsData?.length > 0 && (
          <>
            <Box>
              {appointmentsData.map((item: any, index: number) => {
                const now = DateTime.now();
                const appointmentStart = DateTime.fromISO(item?.scheduledFrom);
                // const appointmentEnd = DateTime.fromISO(item?.scheduledTo);

                const canReschedule =
                  appointmentStart.diff(now, "hours").hours >= 4;
                // const canJoin =
                //   item?.status === "ongoing" &&
                //   appointmentStart.diff(now, "minutes").minutes <= 15 &&
                //   appointmentEnd > now;
                return (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      backgroundColor: "#fff",
                      mb: 2,
                      boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.08)",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box width="64px">
                        {type === "upcoming" ? (
                          <ActiveProfileIcon />
                        ) : (
                          <InactiveProfileIcon />
                        )}
                      </Box>
                      <Box sx={{ width: "calc(100% - 76px)" }}>
                        <Typography
                          sx={{
                            fontSize: "18px",
                            fontWeight: 600,
                            color:
                              type === "upcoming"
                                ? "text.primary"
                                : "text.secondary",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item?.name || "-"}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "text.secondary",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item?.title || "-"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        mt: 2,
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { xs: "flex-start", md: "center" },
                        justifyContent: "space-between",
                        gap: 2,
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection={{ xs: "column", sm: "row" }}
                        gap={2}
                      >
                        <Box display="flex" alignItems="center" gap={2}>
                          <CalendarIcon />
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            {item?.date || "-"}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={2}>
                          <TimeIcon />
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            {item?.startTime} - {item?.endTime}
                          </Typography>
                        </Box>
                      </Box>
                      {type === "upcoming" &&
                        (loadingAppointmentId === item?.id ? (
                          <CircularProgress size={25} />
                        ) : (
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            width={{ xs: "100%", md: "auto" }}
                          >
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => handleCancel(item)}
                              sx={{
                                borderRadius: { xs: "8px", sm: "12px" },
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => openDrawer(item)}
                              disabled={!canReschedule}
                              sx={{
                                borderRadius: { xs: "8px", sm: "12px" },
                              }}
                            >
                              Re-schedule
                            </Button>
                            <Button
                              variant="contained"
                              onClick={() => handleJoinCall(item?.id)}
                              disabled={item?.status !== "ongoing"}
                              sx={{
                                borderRadius: { xs: "8px", sm: "12px" },
                              }}
                            >
                              Join call
                            </Button>
                          </Stack>
                        ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {totalAppointmentCount > pageSize && (
              <Table>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      sx={TablePaginationStyle}
                      count={totalAppointmentCount}
                      page={page}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[pageSize]}
                      onPageChange={handleChangePage}
                      labelDisplayedRows={paginationLabel}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            )}
          </>
        )
      )}
      {!loading && appointmentsData?.length === 0 && (
        <NoDataContainer>
          <Typography variant="body1" color="gray">
            No{" "}
            {type === "upcoming"
              ? "Upcoming"
              : type === "past"
              ? "Past"
              : "Cancelled"}{" "}
            Appointments Available
          </Typography>
        </NoDataContainer>
      )}
    </Box>
  );
};

export default Appointments;
